import type {
  NavPageRecord,
  NavPageSlotRecord,
  PageModel,
  ResolvedSlot,
  SlotIssue,
  SlotIssueCode,
} from "./types";

type BuildPageModelOptions = { includeDisabled?: boolean };

const issue = (code: SlotIssueCode, slotKey: string): SlotIssue => ({ code, slotKey });

const slotIssues = (
  slot: NavPageSlotRecord,
  pagesByKey: Map<string, NavPageRecord>,
): SlotIssue[] => {
  const found: SlotIssue[] = [];

  switch (slot.type) {
    case "collection":
    case "properties":
      if (!slot.targetSchema) {
        found.push(issue("missing-schema", slot.key));
      }
      if (!slot.view) {
        found.push(issue("missing-view-name", slot.key));
      }
      break;
    case "facet-chips":
      if (!slot.targetSchema) {
        found.push(issue("missing-schema", slot.key));
      }
      if (!slot.field) {
        found.push(issue("missing-facet-field", slot.key));
      }
      break;
    case "compose":
      if (!slot.childPage || !pagesByKey.has(slot.childPage)) {
        found.push(issue("missing-child-page", slot.key));
      }
      break;
    case "custom":
      if (!slot.component) {
        found.push(issue("missing-component", slot.key));
      }
      break;
    case "text":
      if (!slot.textValue) {
        found.push(issue("missing-text", slot.key));
      }
      break;
    default: {
      const _exhaustive: never = slot.type;
      return _exhaustive;
    }
  }

  if (slot.bindSource === "slot" && !slot.bindSourceSlot) {
    found.push(issue("missing-bind-source-slot", slot.key));
  }

  return found;
};

const sortSlots = (slots: NavPageSlotRecord[]): NavPageSlotRecord[] =>
  slots.slice().sort((a, b) => a.order - b.order);

const warnSkippedChildPage = (slotKey: string, pageKey: string): void => {
  if (!import.meta.env.DEV) {
    return;
  }
  console.warn(`[page-model] skipping compose child ${pageKey} of ${slotKey}`);
};

const resolveComposeChildren = (
  pages: readonly NavPageRecord[],
  slots: readonly NavPageSlotRecord[],
  slot: NavPageSlotRecord,
  defaultChild: PageModel,
  visiting: ReadonlySet<string>,
  options: BuildPageModelOptions,
): PageModel[] => {
  const defaultKey = defaultChild.page.key;
  const keys: string[] = [defaultKey];
  for (const key of slot.childPages ?? []) {
    if (!keys.includes(key)) {
      keys.push(key);
    }
  }

  const resolved: PageModel[] = [];
  for (const key of keys) {
    if (key === defaultKey) {
      resolved.push(defaultChild);
      continue;
    }
    if (visiting.has(key)) {
      warnSkippedChildPage(slot.key, key);
      continue;
    }
    const child = buildPageModel(pages, slots, key, visiting, options);
    if (!child) {
      warnSkippedChildPage(slot.key, key);
      continue;
    }
    resolved.push(child);
  }

  return resolved;
};

/**
 * Join slots onto a page, sort by `(region, order)`, drop disabled rows unless
 * `includeDisabled` (composer tree — authors must re-enable and see errors),
 * and resolve `type: compose` children. Cyclic embeds are dropped.
 */
export const buildPageModel = (
  pages: readonly NavPageRecord[],
  slots: readonly NavPageSlotRecord[],
  pageKey: string,
  visiting: ReadonlySet<string> = new Set(),
  options: BuildPageModelOptions = {},
): PageModel | undefined => {
  const includeDisabled = options.includeDisabled === true;
  const pagesByKey = new Map<string, NavPageRecord>();
  for (const page of pages) {
    if (!includeDisabled && page.enabled === false) {
      continue;
    }
    if (!pagesByKey.has(page.key)) {
      pagesByKey.set(page.key, page);
    }
  }

  const page = pagesByKey.get(pageKey);
  if (!page) {
    return undefined;
  }

  if (visiting.has(pageKey)) {
    return undefined;
  }

  const nextVisiting = new Set(visiting);
  nextVisiting.add(pageKey);

  const owned = slots.filter(
    (slot) => slot.page === pageKey && (includeDisabled || slot.enabled !== false),
  );

  const resolve = (slot: NavPageSlotRecord): ResolvedSlot => {
    const issues = slotIssues(slot, pagesByKey);

    if (slot.type === "compose" && slot.childPage && nextVisiting.has(slot.childPage)) {
      issues.push(issue("compose-cycle", slot.key));
    }

    let child: PageModel | undefined;
    let children: PageModel[] | undefined;
    if (slot.type === "compose" && slot.childPage && issues.length === 0) {
      child = buildPageModel(pages, slots, slot.childPage, nextVisiting, options);
      if (!child) {
        issues.push(issue("missing-child-page", slot.key));
      } else {
        children = resolveComposeChildren(pages, slots, slot, child, nextVisiting, options);
      }
    }

    return { slot, child, children, issues };
  };

  const resolved = owned.map(resolve);
  const byRegion = (region: NavPageSlotRecord["region"]) =>
    sortSlots(resolved.filter((row) => row.slot.region === region).map((row) => row.slot)).map(
      (slot) => resolved.find((row) => row.slot.key === slot.key) as ResolvedSlot,
    );

  return {
    page,
    body: byRegion("body"),
    tabs: byRegion("tab"),
    rail: byRegion("rail"),
  };
};

const warnedDroppedSlots = new Set<string>();

/** View-less data slots still render (platform default). Incomplete means no object, or a slot bind with no source. */
const isRuntimeDropIssue = (code: SlotIssueCode): boolean => code !== "missing-view-name";

const COMPOSER_INCOMPLETE_CODES: ReadonlySet<SlotIssueCode> = new Set([
  "missing-schema",
  "missing-bind-source-slot",
]);

export const isComposerIncomplete = (row: ResolvedSlot): boolean =>
  row.issues.some((issue) => COMPOSER_INCOMPLETE_CODES.has(issue.code));

const warnDroppedSlot = (row: ResolvedSlot): void => {
  if (!import.meta.env.DEV) {
    return;
  }
  if (warnedDroppedSlots.has(row.slot.key)) {
    return;
  }
  warnedDroppedSlots.add(row.slot.key);
  const codes = row.issues.map((issue) => issue.code).join(", ");
  console.warn(`[page-model] dropping slot ${row.slot.key}: ${codes}`);
};

/** URL / tab-strip key for a slot — `slug` when authored, otherwise `key`. */
export const composedTabKey = (slot: NavPageSlotRecord): string => slot.slug?.trim() || slot.key;

/** Inner-tab URL segment for a child page — strip a leading `page-`. */
export const innerTabKey = (pageKey: string): string =>
  pageKey.startsWith("page-") && pageKey.length > 5 ? pageKey.slice("page-".length) : pageKey;

/** Slots the runtime should render — structural issues fail closed. */
export const visibleSlots = (slots: readonly ResolvedSlot[]): ResolvedSlot[] => {
  const visible: ResolvedSlot[] = [];
  for (const row of slots) {
    if (row.issues.some((issue) => isRuntimeDropIssue(issue.code))) {
      warnDroppedSlot(row);
      continue;
    }
    visible.push(row);
  }
  return visible;
};

/**
 * Record page for a schema given one preferred key (slot `detailPage` or
 * nav-item `detailPage`). Honour the key only when it names an enabled
 * `template: "record"` page whose `targetSchema` matches; otherwise the
 * schema's `isDefault` page, then none (generic record page). Callers that
 * have both keys must try the slot key first and fall through themselves.
 */
export const resolveRecordPage = (
  pages: readonly NavPageRecord[],
  schema: string,
  version: string,
  detailPageKey?: string,
): NavPageRecord | undefined => {
  const records = pages.filter(
    (page) =>
      page.enabled !== false &&
      page.template === "record" &&
      page.targetSchema === schema &&
      (!page.targetVersion || page.targetVersion === version),
  );

  if (detailPageKey) {
    const named = records.find((page) => page.key === detailPageKey);
    if (named) {
      return named;
    }
  }

  return records.find((page) => page.isDefault);
};
